import {GoodgameBitmapEngine} from "../../GoodgameBitmapEngine";
import {ClipLayerInfo} from "../../analyse/ClipLayerInfo";
import {AbstractClipSource} from "../AbstractClipSource";
import {FlattenedImage} from "../FlattenedImage";
import {FrameStrip} from "../FrameStrip";
import {IClipSource} from "../IClipSource";
import {ClipColor} from "../../components/ClipColor";
import {DisplayObjectClipContainer} from "../../container/DisplayObjectClipContainer";
import { ClipSourceOptimizer } from "../ClipSourceOptimizer";

import Bitmap = createjs.Bitmap;
import BitmapData = createjs.BitmapData;
import Container = createjs.Container;
import MovieClip = createjs.MovieClip;
import StageQuality = createjs.StageQuality;
import Matrix2D = createjs.Matrix2D;
import Rectangle = createjs.Rectangle;

export class DisplayObjectClipSource extends AbstractClipSource implements IClipSource {
    /** container to calculate registration point of displayobject. */
    private static containerRegPointFinder: Container = new Container();
    /** rectangle to find position of registration point. */
    private static regPointFinder: Rectangle = new Rectangle();
    public get scaleFactor(): number {
        return this._scaleFactor;
    }
    public get layerInfo(): ClipLayerInfo {
        return this._layerInfo;
    }
    public hasSubLayer: boolean = false;
    /** sourceClass of the DisplayObject */
    protected sourceClass: any;
    /** sourceClassName of the DisplayObject */
    protected sourceClassName: string;
    protected _layerInfo: ClipLayerInfo; // TODO: quick-fix for json

    private _scaleFactor: number = 1;

    /**
     * Converts a DisplayObject class to a BitmapSource
     *
     * @param SourceClass (must be a DisplayObjectClass)
     * @param color that is drawn instead of the tranparent areas
     *
     */
    public convertClass(clipClass: any, colorData: ClipColor[] = null, debugColor: number = 0, hasSubLayer: boolean = false): void {
        this.sourceClass = clipClass;
        this._colorData = colorData;
        this.debugColor = debugColor;
        this.hasSubLayer = hasSubLayer;
        // TODO: @HTML5
        this._cacheId = "";
        this._framesStrip = this.convertDisplayObjectContainer(new this.sourceClass());
    }

    /**
     * Converts a Container Object to a BitmapSource
     *
     * @param SourceClassInstance (must be a Container)
     *
     */
    /*final*/
    protected convertDisplayObjectContainer(source: Object): FrameStrip {
        if (source instanceof DisplayObjectClipContainer) {
            this._layerInfo = new ClipLayerInfo((<DisplayObjectClipContainer>source));
        }

        if (source instanceof MovieClip) {
            return this.convertMovieClip((<MovieClip>source));
        } else if (source instanceof Container) {
            return this.convertSprite((<Container>source));
        } else if (source instanceof BitmapData) {
            let bmp: Bitmap = new Bitmap((<BitmapData>source).canvas);
            let spr: Container = new Container();
            spr.addChild(bmp);
            return this.convertSprite(spr);
        }
        return null;
    }

       /**
     * Renders the content of the current frame of a DisplayObject to FlattenedImage
     *
     * @param SourceClassInstance
     *
     */
    protected renderSingleFrame(source: Container): FlattenedImage {
        DisplayObjectClipSource.containerRegPointFinder.addChild(source);
        DisplayObjectClipSource.regPointFinder = DisplayObjectClipSource.containerRegPointFinder.getBounds();
        DisplayObjectClipSource.containerRegPointFinder.removeChild(source);
        this.addToClipBounds(DisplayObjectClipSource.regPointFinder);

        let flatWidth: number = Math.ceil(source.width * this._scaleFactor);
        let flatHeight: number = Math.ceil(source.height * this._scaleFactor);

        // scaled children break the positions, we need to add an offset to fix those
        let scaledOffsetX = source.children.length > 0 && source.children[0].scaleX < 1 ? flatWidth / 2 : 0;
        let scaledOffsetY = source.children.length > 0 && source.children[0].scaleY < 1 ? flatHeight / 2 : 0;

        let flatRegX: number = -Math.floor(DisplayObjectClipSource.regPointFinder.x * this._scaleFactor) + scaledOffsetX;
        flatWidth += Math.ceil(Math.abs(flatRegX + DisplayObjectClipSource.regPointFinder.x));
        let flatRegY: number = -Math.floor(DisplayObjectClipSource.regPointFinder.y * this._scaleFactor) + scaledOffsetY;
        flatHeight += Math.ceil(Math.abs(flatRegY + DisplayObjectClipSource.regPointFinder.y));

        let flatImage = new FlattenedImage(flatWidth, flatHeight, flatRegX, flatRegY, undefined, true);
        if (this._colorData) {
            this._colorData.forEach((cc) => {
                cc.colorClip(source);
            });
        }
        source.cache(-flatRegX, -flatRegY, flatWidth, flatHeight);

        flatImage.draw(source);

        return flatImage;
    }

    /**
     * Converts a MovieClip to a BitmapSource
     *
     * @param SourceClassInstance
     *
     */
    private convertMovieClip(source: MovieClip): FrameStrip {
        if (this._colorData && source.children.length) {
            // if MC is not played, child will be empty, no point to color
            for (let cC of this._colorData) {
                cC.colorClip(source);
            }
        }
        let cachedBitmaps = ClipSourceOptimizer.ClipMCToBitmaps(source, this, this.renderSingleFrame);
        cachedBitmaps.forEach( bmp => {
            this._framesStrip.addFrame(bmp);
        });
        return this._framesStrip;
    }

    /**
     * Converts a Sprite to a BitmapSource
     *
     * @param SourceClassInstance
     *
     */
    private convertSprite(source: Container): FrameStrip {
        if (source.children.length === 1 && source.children[0] instanceof Bitmap) {
            this._framesStrip.addFrame((source.children[0] as Bitmap).clone());
        } else {
            let frame: FlattenedImage = this.renderSingleFrame(source);
            this._framesStrip.addFrame(ClipSourceOptimizer.flattenedImagesToBitmaps([frame])[0]);
        }
        return this._framesStrip;
    }
}
